import { jsxs, jsx } from 'react/jsx-runtime';
import { createContext, useState, useRef, useEffect } from 'react';
import { Turnkey, AuthClient } from '@turnkey/sdk-browser';
import { useUserSession } from '../hooks/use-session.mjs';

const TurnkeyContext = createContext({
    client: undefined,
    turnkey: undefined,
    passkeyClient: undefined,
    authIframeClient: undefined,
    walletClient: undefined,
    getActiveClient: async () => {
        return undefined;
    },
});
const TurnkeyProvider = ({ config, children, }) => {
    const [turnkey, setTurnkey] = useState(undefined);
    const [passkeyClient, setPasskeyClient] = useState(undefined);
    const [walletClient, setWalletClient] = useState(undefined);
    const [authIframeClient, setAuthIframeClient] = useState(undefined);
    const [client, setClient] = useState(undefined);
    const { session } = useUserSession();
    const iframeInit = useRef(false);
    const TurnkeyAuthIframeContainerId = "turnkey-auth-iframe-container-id";
    const TurnkeyAuthIframeElementId = "turnkey-auth-iframe-element-id";
    const getActiveClient = async () => {
        let currentClient = passkeyClient;
        const currentUser = await turnkey?.getCurrentUser();
        try {
            // check if the iframeClient is active
            await authIframeClient?.getWhoami({
                organizationId: currentUser?.organization.organizationId ??
                    turnkey?.config.defaultOrganizationId,
            });
            currentClient = authIframeClient;
        }
        catch (error) {
            try {
                // if not, check if there's a readWriteSession in localStorage, and try to initialize an iframeClient with it
                const readWriteSession = await turnkey?.getReadWriteSession();
                if (readWriteSession) {
                    const injected = await authIframeClient?.injectCredentialBundle(readWriteSession.credentialBundle);
                    if (injected) {
                        await authIframeClient?.getWhoami({
                            organizationId: currentUser?.organization.organizationId ??
                                turnkey?.config.defaultOrganizationId,
                        });
                        currentClient = authIframeClient;
                    }
                }
            }
            catch (error) {
                // default to using the passkeyClient
            }
        }
        return currentClient;
    };
    useEffect(() => {
        (async () => {
            if (!iframeInit.current) {
                iframeInit.current = true;
                const newTurnkey = new Turnkey(config);
                setTurnkey(newTurnkey);
                setPasskeyClient(newTurnkey.passkeyClient());
                if (config.wallet) {
                    setWalletClient(newTurnkey.walletClient(config.wallet));
                }
                const newAuthIframeClient = await newTurnkey.iframeClient({
                    iframeContainer: document.getElementById(TurnkeyAuthIframeContainerId),
                    iframeUrl: "https://auth.turnkey.com",
                    iframeElementId: TurnkeyAuthIframeElementId,
                });
                setAuthIframeClient(newAuthIframeClient);
            }
        })();
    }, []);
    /**
     * Effect hook that updates the active client based on the current session's authenticated client.
     *
     * This hook listens for changes in the `session` object. If the `session` contains an `authClient`,
     * it determines which client was used for initial authentication by checking the `authClient` key.
     * It then sets the corresponding client (either `authIframeClient`, `passkeyClient`, or `walletClient`)
     * as the active client using the `setClient` function.
     *
     * If the `session` changes, the `authClient` will be recomputed and the active client will be
     * updated accordingly.
     */
    useEffect(() => {
        if (session?.authClient) {
            const client = {
                [AuthClient.Iframe]: authIframeClient,
                [AuthClient.Passkey]: passkeyClient,
                [AuthClient.Wallet]: walletClient,
            }[session?.authClient];
            setClient(client);
        }
    }, [session]);
    return (jsxs(TurnkeyContext.Provider, { value: {
            client,
            turnkey,
            passkeyClient,
            authIframeClient,
            walletClient,
            getActiveClient,
        }, children: [children, jsx("div", { className: "", id: TurnkeyAuthIframeContainerId, style: { display: "none" } })] }));
};

export { TurnkeyContext, TurnkeyProvider };
//# sourceMappingURL=TurnkeyContext.mjs.map
